import { encrypt, decrypt } from './crypto';
import { AppData, DEFAULT_APP_DATA } from './types';
import { AppDataSchema } from './schemas';

const DATA_KEY = 'aussibooks_data';
const HASH_KEY = 'aussibooks_hash';
const PIN_HASH_KEY = 'aussibooks_pin_hash';
const PIN_ENC_PWD_KEY = 'aussibooks_pin_enc_pwd';
const BIO_CRED_KEY = 'aussibooks_bio_cred';
const BIO_ENC_PWD_KEY = 'aussibooks_bio_enc_pwd';
const BIO_KEY_KEY = 'aussibooks_bio_key';
const EMAIL_KEY = 'aussibooks_email';

// Email management
export function getStoredEmail(): string {
  return localStorage.getItem(EMAIL_KEY) || '';
}

export function setStoredEmail(email: string): void {
  localStorage.setItem(EMAIL_KEY, email);
}

export function hasExistingAccount(): boolean {
  return localStorage.getItem(HASH_KEY) !== null;
}

export function getPasswordHash(): string | null {
  return localStorage.getItem(HASH_KEY);
}

export function setPasswordHash(hash: string): void {
  localStorage.setItem(HASH_KEY, hash);
}

// PIN management
export function hasPinSetup(): boolean {
  return localStorage.getItem(PIN_HASH_KEY) !== null;
}

export function getPinHash(): string | null {
  return localStorage.getItem(PIN_HASH_KEY);
}

export function setPinHash(hash: string): void {
  localStorage.setItem(PIN_HASH_KEY, hash);
}

export function getPinEncryptedPassword(): string | null {
  return localStorage.getItem(PIN_ENC_PWD_KEY);
}

export function setPinEncryptedPassword(encrypted: string): void {
  localStorage.setItem(PIN_ENC_PWD_KEY, encrypted);
}

export function clearPin(): void {
  localStorage.removeItem(PIN_HASH_KEY);
  localStorage.removeItem(PIN_ENC_PWD_KEY);
}

// Biometric management
export function hasBiometricSetup(): boolean {
  return localStorage.getItem(BIO_CRED_KEY) !== null;
}

export function getBiometricCredentialId(): string | null {
  return localStorage.getItem(BIO_CRED_KEY);
}

export function setBiometricCredentialId(id: string): void {
  localStorage.setItem(BIO_CRED_KEY, id);
}

export function getBiometricEncryptedPassword(): string | null {
  return localStorage.getItem(BIO_ENC_PWD_KEY);
}

export function setBiometricEncryptedPassword(encrypted: string): void {
  localStorage.setItem(BIO_ENC_PWD_KEY, encrypted);
}

export function getBiometricKey(): string | null {
  return localStorage.getItem(BIO_KEY_KEY);
}

export function setBiometricKey(key: string): void {
  localStorage.setItem(BIO_KEY_KEY, key);
}

export function clearBiometric(): void {
  localStorage.removeItem(BIO_CRED_KEY);
  localStorage.removeItem(BIO_ENC_PWD_KEY);
  localStorage.removeItem(BIO_KEY_KEY);
}

export async function saveData(data: AppData, password: string): Promise<void> {
  const json = JSON.stringify(data);
  const encrypted = await encrypt(json, password);
  localStorage.setItem(DATA_KEY, encrypted);
}

export async function loadData(password: string): Promise<AppData> {
  const encrypted = localStorage.getItem(DATA_KEY);
  if (!encrypted) return { ...DEFAULT_APP_DATA };
  const json = await decrypt(encrypted, password);
  const parsed = JSON.parse(json);
  return AppDataSchema.parse(parsed) as AppData;
}

export async function exportData(password: string): Promise<string> {
  const encrypted = localStorage.getItem(DATA_KEY);
  if (!encrypted) {
    return await encrypt(JSON.stringify(DEFAULT_APP_DATA), password);
  }
  return encrypted;
}

export async function importData(encryptedData: string, password: string): Promise<AppData> {
  const json = await decrypt(encryptedData, password);
  const parsed = JSON.parse(json);
  return AppDataSchema.parse(parsed) as AppData;
}

export function clearAllData(): void {
  localStorage.removeItem(DATA_KEY);
  localStorage.removeItem(HASH_KEY);
  localStorage.removeItem(PIN_HASH_KEY);
  localStorage.removeItem(PIN_ENC_PWD_KEY);
  localStorage.removeItem(BIO_CRED_KEY);
  localStorage.removeItem(BIO_ENC_PWD_KEY);
  localStorage.removeItem(BIO_KEY_KEY);
  localStorage.removeItem(EMAIL_KEY);
  // Clear bank sync related keys
  localStorage.removeItem('aussibooks_basiq_last_sync');
  localStorage.removeItem('aussibooks_basiq_user_id');
  localStorage.removeItem('aussibooks_last_backup');
  // Clear session
  sessionStorage.removeItem('_ab_session');
}
